import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  writeBatch,
  Timestamp
} from 'firebase/firestore';

/**
 * OldJobsManagement - Admin Dashboard Component
 *
 * Features:
 * 1. View all jobs by status (expired, cancelled, active)
 * 2. Filter by age (30, 60, 90+ days)
 * 3. Archive jobs (move to archivedJobs collection for audit trail)
 * 4. Delete jobs permanently (with confirmation)
 * 5. Bulk actions for efficiency
 */
function OldJobsManagement() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [filter, setFilter] = useState({
    status: 'all',
    ageInDays: 30
  });
  const [stats, setStats] = useState({
    total: 0,
    expired: 0,
    cancelled: 0,
    active: 0,
    older30: 0,
    older60: 0,
    older90: 0
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const jobsSnapshot = await getDocs(collection(db, 'staffingRequests'));

      const now = new Date();
      const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now - 60 * 24 * 60 * 60 * 1000);
      const ninetyDaysAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);

      let expired = 0, cancelled = 0, active = 0;
      let older30 = 0, older60 = 0, older90 = 0;

      const jobsData = jobsSnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const createdAt = data.createdAt?.toDate?.() || data.createdAt;
        const status = data.status || 'unknown';

        // Count by status
        if (status === 'expired') expired++;
        else if (status === 'cancelled') cancelled++;
        else if (status === 'active') active++;

        // Count by age
        if (createdAt) {
          if (createdAt < ninetyDaysAgo) older90++;
          else if (createdAt < sixtyDaysAgo) older60++;
          else if (createdAt < thirtyDaysAgo) older30++;
        }

        return {
          id: docSnap.id,
          ...data,
          createdAt,
          ageInDays: createdAt ? Math.floor((now - createdAt) / (1000 * 60 * 60 * 24)) : 0
        };
      });

      // Sort by age (oldest first)
      jobsData.sort((a, b) => (b.ageInDays || 0) - (a.ageInDays || 0));

      setJobs(jobsData);
      setStats({
        total: jobsData.length,
        expired,
        cancelled,
        active,
        older30,
        older60,
        older90
      });
      setSelectedJobs(new Set());
    } catch (error) {
      console.error('Error loading jobs:', error);
      alert('Failed to load jobs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredJobs = () => {
    const now = new Date();
    const cutoffDate = new Date(now - filter.ageInDays * 24 * 60 * 60 * 1000);

    return jobs.filter(job => {
      // Filter by status
      if (filter.status !== 'all' && job.status !== filter.status) {
        return false;
      }
      // Filter by age
      if (job.createdAt && job.createdAt >= cutoffDate) {
        return false;
      }
      return true;
    });
  };

  const archiveJob = async (jobId) => {
    if (!window.confirm('Archive this job? It will be moved to the archive for record keeping.')) {
      return;
    }

    setProcessing(jobId);
    try {
      // Get the job data
      const jobRef = doc(db, 'staffingRequests', jobId);
      const jobSnap = await getDoc(jobRef);

      if (!jobSnap.exists()) {
        alert('Job not found');
        return;
      }

      const jobData = jobSnap.data();

      // Move to archivedJobs collection
      await setDoc(doc(db, 'archivedJobs', jobId), {
        ...jobData,
        archivedAt: Timestamp.now(),
        archivedBy: 'admin',
        originalCollection: 'staffingRequests'
      });

      // Delete from staffingRequests
      await deleteDoc(jobRef);

      alert('Job archived successfully!');
      loadJobs();
    } catch (error) {
      console.error('Error archiving job:', error);
      alert('Failed to archive job: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('PERMANENTLY DELETE this job? This action cannot be undone!')) {
      return;
    }
    if (!window.confirm('Are you REALLY sure? This will permanently remove all data for this job.')) {
      return;
    }

    setProcessing(jobId);
    try {
      await deleteDoc(doc(db, 'staffingRequests', jobId));
      alert('Job deleted permanently!');
      loadJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Failed to delete job: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const bulkArchive = async () => {
    if (selectedJobs.size === 0) {
      alert('Please select jobs to archive');
      return;
    }

    if (!window.confirm(`Archive ${selectedJobs.size} selected jobs?`)) {
      return;
    }

    setProcessing('bulk');
    try {
      const batch = writeBatch(db);

      for (const jobId of selectedJobs) {
        const jobRef = doc(db, 'staffingRequests', jobId);
        const jobSnap = await getDoc(jobRef);

        if (jobSnap.exists()) {
          const jobData = jobSnap.data();

          // Add to archive
          const archiveRef = doc(db, 'archivedJobs', jobId);
          batch.set(archiveRef, {
            ...jobData,
            archivedAt: Timestamp.now(),
            archivedBy: 'admin',
            originalCollection: 'staffingRequests'
          });

          // Delete from staffingRequests
          batch.delete(jobRef);
        }
      }

      await batch.commit();
      alert(`${selectedJobs.size} jobs archived successfully!`);
      loadJobs();
    } catch (error) {
      console.error('Error bulk archiving:', error);
      alert('Failed to archive jobs: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const bulkDelete = async () => {
    if (selectedJobs.size === 0) {
      alert('Please select jobs to delete');
      return;
    }

    if (!window.confirm(`PERMANENTLY DELETE ${selectedJobs.size} selected jobs?`)) {
      return;
    }
    if (!window.confirm('This action CANNOT be undone! Are you absolutely sure?')) {
      return;
    }

    setProcessing('bulk');
    try {
      const batch = writeBatch(db);

      for (const jobId of selectedJobs) {
        batch.delete(doc(db, 'staffingRequests', jobId));
      }

      await batch.commit();
      alert(`${selectedJobs.size} jobs deleted permanently!`);
      loadJobs();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Failed to delete jobs: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const toggleJobSelection = (jobId) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const selectAllFiltered = () => {
    const filtered = getFilteredJobs();
    const newSelected = new Set(filtered.map(j => j.id));
    setSelectedJobs(newSelected);
  };

  const clearSelection = () => {
    setSelectedJobs(new Set());
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      completed: 'bg-blue-100 text-blue-800',
      unknown: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || colors.unknown;
  };

  const filteredJobs = getFilteredJobs();

  if (loading) {
    return <div className="p-4">Loading jobs data...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Old Jobs Management</h2>

      {/* Statistics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Jobs</div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
          <div className="text-sm text-gray-600">Expired</div>
        </div>
        <div className="bg-gray-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-600">{stats.cancelled}</div>
          <div className="text-sm text-gray-600">Cancelled</div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.older30}</div>
          <div className="text-sm text-gray-600">30-60 days</div>
        </div>
        <div className="bg-orange-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.older60}</div>
          <div className="text-sm text-gray-600">60-90 days</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-700">{stats.older90}</div>
          <div className="text-sm text-gray-600">90+ days</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="border rounded px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Older Than</label>
            <select
              value={filter.ageInDays}
              onChange={(e) => setFilter({ ...filter, ageInDays: parseInt(e.target.value) })}
              className="border rounded px-3 py-2"
            >
              <option value="0">All Ages</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
            </select>
          </div>
          <div className="flex-1"></div>
          <div className="text-sm text-gray-600">
            Showing {filteredJobs.length} of {stats.total} jobs
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-blue-50 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <span className="font-medium">{selectedJobs.size} selected</span>
          <button
            onClick={selectAllFiltered}
            className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
          >
            Select All ({filteredJobs.length})
          </button>
          <button
            onClick={clearSelection}
            className="bg-gray-400 text-white px-3 py-1 rounded text-sm hover:bg-gray-500"
          >
            Clear Selection
          </button>
          <div className="flex-1"></div>
          <button
            onClick={bulkArchive}
            disabled={selectedJobs.size === 0 || processing === 'bulk'}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:bg-gray-400"
          >
            {processing === 'bulk' ? 'Processing...' : `Archive Selected (${selectedJobs.size})`}
          </button>
          <button
            onClick={bulkDelete}
            disabled={selectedJobs.size === 0 || processing === 'bulk'}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
          >
            Delete Selected
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      {filteredJobs.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
          No jobs match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 border">
                  <input
                    type="checkbox"
                    checked={selectedJobs.size === filteredJobs.length && filteredJobs.length > 0}
                    onChange={() => {
                      if (selectedJobs.size === filteredJobs.length) {
                        clearSelection();
                      } else {
                        selectAllFiltered();
                      }
                    }}
                  />
                </th>
                <th className="px-4 py-3 border text-left">Job Title</th>
                <th className="px-4 py-3 border text-left">Status</th>
                <th className="px-4 py-3 border text-left">Recruiter ID</th>
                <th className="px-4 py-3 border text-left">Created</th>
                <th className="px-4 py-3 border text-center">Age</th>
                <th className="px-4 py-3 border text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job) => (
                <tr
                  key={job.id}
                  className={`hover:bg-gray-50 ${selectedJobs.has(job.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 py-3 border text-center">
                    <input
                      type="checkbox"
                      checked={selectedJobs.has(job.id)}
                      onChange={() => toggleJobSelection(job.id)}
                    />
                  </td>
                  <td className="px-4 py-3 border">
                    <div className="font-medium">{job.jobTitle || job.title || 'No title'}</div>
                    <div className="text-xs text-gray-500">{job.id}</div>
                  </td>
                  <td className="px-4 py-3 border">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(job.status)}`}>
                      {job.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 border">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {job.recruiterId ? job.recruiterId.substring(0, 12) + '...' : 'N/A'}
                    </code>
                  </td>
                  <td className="px-4 py-3 border text-sm">
                    {formatDate(job.createdAt)}
                  </td>
                  <td className="px-4 py-3 border text-center">
                    <span className={`font-semibold ${
                      job.ageInDays >= 90 ? 'text-red-600' :
                      job.ageInDays >= 60 ? 'text-orange-600' :
                      job.ageInDays >= 30 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {job.ageInDays}d
                    </span>
                  </td>
                  <td className="px-4 py-3 border text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => archiveJob(job.id)}
                        disabled={processing === job.id}
                        className="bg-yellow-500 text-white px-3 py-1 rounded text-xs hover:bg-yellow-600 disabled:bg-gray-400"
                        title="Archive (keeps record)"
                      >
                        {processing === job.id ? '...' : 'Archive'}
                      </button>
                      <button
                        onClick={() => deleteJob(job.id)}
                        disabled={processing === job.id}
                        className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700 disabled:bg-gray-400"
                        title="Delete permanently"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend / Help */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4 mt-6">
        <h4 className="font-semibold mb-2">How It Works:</h4>
        <ul className="text-sm space-y-1">
          <li><strong>Archive:</strong> Moves job to <code>archivedJobs</code> collection. Keeps audit trail, removes from active list.</li>
          <li><strong>Delete:</strong> Permanently removes job. Cannot be undone. Use with caution.</li>
          <li><strong>Automatic Cleanup:</strong> System auto-deletes only truly orphaned jobs (no owner, no workflow).</li>
          <li><strong>Recommendation:</strong> Archive expired/cancelled jobs older than 90 days for clean data.</li>
        </ul>
      </div>

      {/* Refresh Button */}
      <div className="mt-4 text-right">
        <button
          onClick={loadJobs}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Refresh Data
        </button>
      </div>
    </div>
  );
}

export default OldJobsManagement;
